import useUpdateEffect from '@/common/hook/updateEffect/useUpdateEffect';
import { FC, useEffect, useMemo, useRef } from 'react';
import { useMethods } from '@lhb/hook';
import { isSameCommunity } from '../../communityMainList';
import { getALQMarkerNumber } from '../../alqCommunityNumber';
import { Typography } from 'antd';
import styles from '../index.module.less';
import { showMarker } from '@/common/utils/map';
import IconFont from '@/common/components/IconFont';
import { targetLegendIcon } from '../../../ts-config';
import { mapContainer } from '..';
import { bigdataAction } from '@/common/utils/bigdata';
import { isArray } from '@lhb/func';
import { v4 } from 'uuid';
import AmapRailOfBC from '@/common/components/business/AmapRailOfBC';

const { Paragraph } = Typography;

/**
 * @Description 周边配套
 */
const CommonRimMarker:FC<{
  mapIns: any;
  mapDom: any;
  data: Record<string, any[]>;
  isALQCommunity?: boolean;
  contentMarkerRef: any;
  curDetail: any;
  setCurDetail: any;
  curZoom: number; // 地图zoom
  curBounds: any; // 可视范围
  handleDetailToNull: () => void;
  onClickMarker?: () => void;
  // spotEvaluationConfig: any; // 点位测评配置
}> = ({
  mapIns,
  mapDom,
  data,
  isALQCommunity = false,
  contentMarkerRef,
  curDetail,
  setCurDetail,
  curZoom,
  curBounds,
  // spotEvaluationConfig,
  handleDetailToNull,
  onClickMarker = () => {}
}) => {
  const massListRef = useRef<any>([]);
  const numberMarkersRef = useRef<{ marker: any; onClick:() => void }[]>([]);
  const activeMarker = useRef<any>(null);
  const ownedContentMarkerRef = useRef<any>(null);
  const activePolygon = useRef<any>(null);
  const contentMarkerTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const dragPixelRef = useRef<any>(null); // marker 拖拽时pixel的缓存

  // 商业和其他的的数据格式不一样，统一成相同的格式
  const targetFormattingData = useMemo(() => {
    const targetData = Object.values?.(data)?.find?.((item) => isArray(item) && item.length > 0);
    // if (targetData?.[0]?.isCommercial || targetData?.[0]?.isCommunity) {
    // 商业
    if (targetData?.[0]?.isCommercial) {
      return targetData.map((item: any) => {
        item.uid = item.uid || v4();
        const { polygon } = item || {};
        if (isArray(polygon) && polygon.length) {
          item.usablePolygon = polygon;
        }
        return item;
      });
    }
    // 其他
    if (isArray(targetData) && targetData.length) {
      return targetData.map((item: any) => {
        item.uid = item.uid || v4();
        const { polygonList } = item || {};
        if (isArray(polygonList) && polygonList.length) {
          const targetPolygon = polygonList.filter(item => +item.lng && +item.lat).map(item => [Number(item.lng), Number(item.lat)]);
          item.usablePolygon = targetPolygon;
        }
        return item;
      });
    }
    return [];
  }, [data]);

  const methods = useMethods({
    createContentMarker(value, tag, activeMarker = null) {
      const dom = document.querySelector(`.${mapContainer}`);
      var pixel = mapIns.lngLatToContainer([value?.lng, value?.lat]);
      const { x, y } = pixel;
      const offset = [0, 0];
      let topBottom = 'top';
      let leftRight = 'center';
      if (y < 200) {
        topBottom = 'top';
      } else if ((dom?.clientHeight || 0) - y < 200) {
        topBottom = 'bottom';
        offset[1] = 0;
      }
      if (x < 200) {
        leftRight = 'left';
      } else if ((dom?.clientWidth || 0) - x < 200) {
        leftRight = 'right';
      }

      contentMarkerRef.current && mapIns.remove(contentMarkerRef.current);
      contentMarkerRef.current = new window.AMap.Marker({
        position: [value.lng, value.lat],
        anchor: `${topBottom}-${leftRight}`,
        offset,
        zIndex: isALQCommunity ? 530 : 60,
        extData: {
          tag
        },
        draggable: true, // 主动设置可拖拽，但是在拖拽的时候不执行来达到拖拽时卡片抖动的场景
      });
      ownedContentMarkerRef.current = contentMarkerRef.current;
      if (contentMarkerTimerRef.current) clearTimeout(contentMarkerTimerRef.current);
      const contentMarker = contentMarkerRef.current;
      contentMarkerTimerRef.current = setTimeout(() => {
        contentMarker?.on?.('dragstart', (e) => {
          dragPixelRef.current = new window.AMap.Pixel(e.target._points[0] - e.pixel.x, e.target._points[1] - e.pixel.y);
          contentMarker.setOffset(dragPixelRef.current);
        });
        contentMarker?.on?.('dragend', (e) => {
          const markerLeftTop = [dragPixelRef.current.x + e.pixel.x, dragPixelRef.current.y + e.pixel.y];
          // 转换为经纬度坐标
          const lnglat = mapIns.containerToLngLat(new window.AMap.Pixel(markerLeftTop[0], markerLeftTop[1]));
          contentMarker.setOffset(new window.AMap.Pixel(0, 0));
          contentMarker.setPosition(lnglat);
        });
      }, 100);
      mapIns.add(contentMarkerRef.current);
      onClickMarker?.();
      showMarker(contentMarkerRef.current,
        <TargetBrand
          data={value}
          mapIns={mapIns}
          marker={contentMarkerRef.current}
          activeMarker={activeMarker}
          handleDetailToNull={() => {
            activePolygon.current && mapIns.remove(activePolygon.current);
            handleDetailToNull();
          }}
          tag={tag}
        />);
    },
    createMass(data, key) {
      if (!data.length) return;
      const value = data.map((item) => ({
        lnglat: [+item.lng, +item.lat],
        detail: item,
        ...item,
      }));

      var mass = new window.AMap.MassMarks(value, {
        opacity: 1,
        cursor: 'pointer',
        style: {
          url: `${targetLegendIcon?.[key]}`,
          size: [24, 29],
          anchor: 'bottom-center'
        },
        zooms: [1, 20],
        zIndex: 500, // 不然会被showIndoorMap遮挡
      });
      mass.setMap(mapIns);

      mass.on('click', ({ data }) => {
        methods.handleMarkerClick(data, key);
      });
      massListRef.current.push(mass);
    },
    createActiveMarker(value, key) {
      const index = data[key]?.findIndex(item => isSameCommunity(item, value)) ?? -1;
      const number = isALQCommunity && key === 'ZHU_ZHAI' ? getALQMarkerNumber(value, index) : '';
      const marker = new window.AMap.Marker({
        position: [value.lng, value.lat],
        content: number ? methods.createNumberContent(number, true) : `<img class="storeMarkerActive" src="${targetLegendIcon?.[key]}"/>`,
        anchor: 'bottom-center',
        // 编号气泡的三角形伸出底部 5px，选中卡片从同一坐标点展开；上移后避免被卡片遮挡。
        ...(number ? { offset: new window.AMap.Pixel(0, -6), zIndex: 520 } : {}),
        extData: value
      });
      activeMarker.current && mapIns.remove(activeMarker.current);
      activeMarker.current = marker;
      methods.createContentMarker(value, key, marker);
      mapIns.add(marker);
    },
    createActivePolygonMarker(value) {
      activePolygon.current && mapIns.remove(activePolygon.current);
      if (value?.overlapDeleteFlag === true) return;
      const { polygonList, polygon, isCommercial } = value || {};
      // 当是商业时,必须有polygon才能绘制 || 非商业时，必须有polygonList才能绘制
      if (isCommercial && !value?.polygon?.length || (!isCommercial && !value?.polygonList?.length)) {
        return;
      }

      const polygonMarker = new window.AMap.Polygon({
        path: isCommercial ? polygon : polygonList.map((item: any) => [+item?.lng, +item?.lat]),
        fillColor: '#006AFF',
        strokeOpacity: 1,
        fillOpacity: 0.2,
        strokeColor: '#4792FD',
        strokeWeight: 2,
      });

      activePolygon.current = polygonMarker;
      mapIns.add(polygonMarker);
    },
    clearMarker(ownerMap = mapIns) {
      if (contentMarkerTimerRef.current) clearTimeout(contentMarkerTimerRef.current);
      if (ownedContentMarkerRef.current) {
        ownerMap?.remove(ownedContentMarkerRef.current);
        if (contentMarkerRef.current === ownedContentMarkerRef.current) contentMarkerRef.current = null;
        ownedContentMarkerRef.current = null;
      }
      massListRef.current?.map((mass) => {
        mass?.clear();
        mass?.setMap(null);
      });
      massListRef.current = [];
      numberMarkersRef.current.forEach(({ marker, onClick }) => {
        marker.off('click', onClick);
        ownerMap?.remove(marker);
      });
      numberMarkersRef.current = [];
      activeMarker.current && ownerMap?.remove(activeMarker.current);
      activePolygon.current && ownerMap?.remove(activePolygon.current);
      activeMarker.current = null;
      activePolygon.current = null;
    },
    createNumberContent(number: string, active = false) {
      const content = document.createElement('div');
      content.className = `${styles.communityNumberMarker}${active ? ` ${styles.communityNumberMarkerActive}` : ''}`;
      // 接口编号以纯文本填入 DOM，避免拼接 HTML。
      const label = document.createElement('span');
      label.className = styles.communityNumberLabel;
      label.textContent = number;
      content.appendChild(label);
      content.title = number;
      return content;
    },
    createNumberMarker(value, key, number: string) {
      const marker = new window.AMap.Marker({
        position: [value.lng, value.lat],
        content: methods.createNumberContent(number),
        anchor: 'bottom-center',
        // 未选中编号气泡与选中态保持相同偏移，避免选中卡片遮挡其底部三角。
        offset: new window.AMap.Pixel(0, -6),
        zIndex: 510,
        extData: value,
      });
      const onClick = () => methods.handleMarkerClick(value, key);
      marker.on('click', onClick);
      numberMarkersRef.current.push({ marker, onClick });
      mapIns.add(marker);
    },
    handleMarkerClick(value, key) {
      handleDetailToNull();
      setCurDetail(value);
      // 动态改变 Marker 的图标和尺寸
      activeMarker.current && mapIns.remove(activeMarker.current);
      methods.createActiveMarker(value, key);
      methods.createActivePolygonMarker(value);
      // /spotevaluation/history?modelConfigId=23 查询例如跳转页未配置菜单，写死op_feat，op_menu
      bigdataAction('c0c5c3c9-3fba-43cb-89d9-9cdcdb87f650', 'spotEvaluation',
        '点位测评', '详情页-地图图标', '点击了详情页-地图图标', { op_block: '', op_event: '', });
    },
  });
  useEffect(() => {
    if (!mapIns) return;
    Object.keys(data)?.forEach((item) => {
      const iconData = data[item].filter((value, index) => {
        const number = isALQCommunity && item === 'ZHU_ZHAI' ? getALQMarkerNumber(value, index) : '';
        if (!number) return true;
        methods.createNumberMarker(value, item, number);
        return false;
      });
      methods.createMass(iconData, item);
      // data[item].map((value) => {
      //   createMarker(value, item);
      // });
    });
    return () => methods.clearMarker(mapIns);
  }, [mapIns, data, isALQCommunity, methods]);

  useEffect(() => {
    if (!mapIns) return;
    let curValue;
    activeMarker.current && mapIns.remove(activeMarker.current);
    activePolygon.current && mapIns.remove(activePolygon.current);
    Object.keys(data).reduce((acc, key) => {
      if (acc) return acc;
      data[key].some((item) => {
        if (key === 'ZHU_ZHAI' ? isSameCommunity(item, curDetail) : item.lng === curDetail?.lng && item.lat === curDetail?.lat) {
          acc = true;
          curValue = {
            ...item,
            tag: key
          };
          return true;
        };
        return false;
      });
      return acc;
    }, false);
    if (curValue) {
      methods.createActiveMarker(curValue, curValue.tag);
      methods.createActivePolygonMarker(curValue);
    }
  }, [mapIns, curDetail, data, isALQCommunity, methods]);

  useUpdateEffect(() => {
    const tag = contentMarkerRef.current?.getExtData()?.tag;
    if (tag && !data[tag]?.length) {
      contentMarkerRef.current && mapIns?.remove(contentMarkerRef.current);
    }
  }, [data]);

  return <div>
    {/* 展示围栏 */}
    {/* {
      targetFormattingData?.[0]?.isCommercial || targetFormattingData?.[0]?.isCommunity ? <AmapRailOfBC
        data={targetFormattingData}
        mapIns={mapIns}
        curZoom={curZoom}
        curBounds={curBounds}
      /> : <></>
    } */}
    <AmapRailOfBC
      data={targetFormattingData}
      mapIns={mapIns}
      mapDom={mapDom}
      curZoom={curZoom}
      curBounds={curBounds}
      // spotEvaluationConfig={spotEvaluationConfig}
    />
  </div>;
};

const TargetBrand = ({
  data,
  mapIns,
  marker,
  activeMarker,
  handleDetailToNull,
  tag
}) => {
  const showText = tag === 'ZHU_ZHAI'; // 小区类型展示房价等字段
  const showBusinessText = tag === 'GOU_WU'; // 图例是商业类型时
  return <div className={styles.competitorOverview}>
    <IconFont
      iconHref={'iconic_close_colour_seven'}
      className={styles.closeIcon}
      onClick={() => {
        handleDetailToNull();
        mapIns.remove(marker);
        mapIns.remove(activeMarker);
      }}
    />
    {/* <div className='fs-14 bold pr-16'>{data?.name}</div> */}
    <Paragraph
      style={{ width: 170, marginBottom: 0 }}
      ellipsis={{
        tooltip: data?.name,
        rows: 2,
      }}>
      <span className='fs-14 bold pr-16'>
        {data?.name}
      </span>
    </Paragraph>
    {showText
      ? <div className={styles.poiInfo}>
        {data.housePrice
          ? <div className={styles.poiLabel}>参考房价
            <span className={styles.poiValue}>{data.housePrice}</span>
          </div> : ''}
        {data.liveNum ? <div className={styles.poiLabel}>居住人口
          <span className={styles.poiValue}>{data.liveNum || '-'}</span>
        </div> : ''}
        {data.houseHolds ? <div className={styles.poiLabel}>户数
          <span className={styles.poiValue}>{data.houseHolds || '-'}</span>
        </div> : ''}
        {data.buildTime
          ? <div className={styles.poiLabel}>建筑年份
            <span className={styles.poiValue}>{data.buildTime}</span>
          </div> : ''}
        {
          data?.address ? <div className={styles.poiLabel}>地址
            <span className={styles.poiValue}>{data.address}</span>
          </div> : <></>
        }
      </div> : <>
        {
          showBusinessText && <div className={styles.poiInfo}>
            <div className={styles.poiLabel}>开业时间：
              <span className={styles.poiValue}>{data.openingTime || '-'}</span>
            </div>
            <div className={styles.poiLabel}>商业建筑面积：
              <span className={styles.poiValue}>{data.commercialVolume || '-'}㎡</span>
            </div>
            <div className={styles.poiLabel}>地址：
              <span className={styles.poiValue}>{data?.address || '-'}</span>
            </div>
          </div>
        }
        {
          !showBusinessText && data?.address ? <div className='c-999 fs-12 mt-6'>
            {data?.address}
          </div> : <></>
        }
      </>}
  </div>;
};
export default CommonRimMarker;
